/**
 * Extract quick-reply candidates from the bot's clarification text.
 *
 * V2.6.1 (2026-04-20). Brian's live-smoke complaint: when the bot asked
 * *"Do you want top 5 by total event gross … Or top 5 compared to the
 * same point in the run (pacing) … say realtime or settlement"*, the
 * quick-reply strip showed three random gallery questions like *"What
 * was net revenue at Cherry Festival 2026?"* — none of them answered the
 * actual ask.
 *
 * This helper detects clarification-shaped messages and pulls candidate
 * answers out of the text itself. When at least 2 candidates surface we
 * use them as quick replies INSTEAD of the gallery; otherwise the
 * caller's gallery flow runs as before.
 *
 * Detection heuristics (any one triggers extraction):
 *   - "**X** … **Y**" — markdown-bold phrases the bot used to highlight
 *     options (the most reliable signal — bot intentionally bolds choices).
 *   - "say X or Y" / "say X, Y or Z" — explicit literal-answer pattern.
 *   - "Do you want X or Y" — disjunction question pattern.
 *   - Bullet-list lines starting with "- " or "• " inside a question.
 *
 * Extraction rules:
 *   - Phrases must be 1–6 words after stop-word trimming.
 *   - Drop trailing punctuation, parenthetical hints like "(pacing)", and
 *     filler phrases.
 *   - Dedupe case-insensitively.
 *   - Cap at 4 candidates so the strip stays scannable.
 */

const MAX_REPLIES = 4
const MAX_WORDS_PER_REPLY = 6

const STOP_WORDS_AT_END = /\s+(?:please|now|today|right\s+now|first|next|too)\.?$/i

function trimCandidate(raw: string): string {
  let s = raw.trim()
  // Strip surrounding markdown markers.
  s = s.replace(/^[*_`"'\s]+|[*_`"'\s]+$/g, '')
  // Drop trailing parenthetical hint: "pacing (not full-event close)" → "pacing"
  s = s.replace(/\s*\([^)]*\)\s*$/, '').trim()
  // Drop trailing punctuation.
  s = s.replace(/[?.!,;:—–-]+$/g, '').trim()
  // Drop trailing filler.
  s = s.replace(STOP_WORDS_AT_END, '').trim()
  return s
}

function isLikelySentence(s: string): boolean {
  // Reject anything that looks like a full sentence rather than an answer.
  if (s.length === 0) return true
  const words = s.split(/\s+/)
  if (words.length === 0 || words.length > MAX_WORDS_PER_REPLY) return true
  if (/[.?!]/.test(s)) return true
  return false
}

function dedupe(candidates: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const c of candidates) {
    const key = c.toLowerCase().replace(/\s+/g, ' ').trim()
    if (!key) continue
    if (seen.has(key)) continue
    seen.add(key)
    out.push(c)
  }
  return out
}

/**
 * Try the markdown-bold pattern first — most reliable signal.
 */
function extractBoldOptions(text: string): string[] {
  const out: string[] = []
  const re = /\*\*([^*\n]{1,80})\*\*/g
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    const c = trimCandidate(m[1])
    if (c && !isLikelySentence(c)) out.push(c)
  }
  return out
}

// Fragments starting with clause-starter pronouns / adverbs are usually
// the rest of the bot's sentence, not another answer option. Reject so
// "say realtime or settlement and I'll compare both" doesn't surface
// "I'll compare both" as a bogus quick reply (Codex HIGH 2026-04-21).
const CLAUSE_STARTER = /^(?:i|i'll|i'd|i'm|we|we'll|we'd|you|you'll|they|he|she|it|then|so|and|but|otherwise|either|both|neither)\b/i

function isClauseContinuation(s: string): boolean {
  return CLAUSE_STARTER.test(s.trim())
}

/**
 * "say X or Y" / "say X, Y or Z" — explicit answer hint.
 */
function extractSayOptions(text: string): string[] {
  const out: string[] = []
  const re = /\bsay\s+([^.?!\n]{2,80})/gi
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    const segment = m[1]
    // Split on commas + 'or'/'and'.
    for (const part of segment.split(/\s*(?:,|\bor\b|\band\b)\s*/i)) {
      if (isClauseContinuation(part)) continue
      const c = trimCandidate(part)
      if (c && !isLikelySentence(c)) out.push(c)
    }
  }
  return out
}

/**
 * "Do you want X or Y" — disjunction question pattern.
 */
function extractDisjunctionOptions(text: string): string[] {
  const out: string[] = []
  const re = /\b(?:do\s+you\s+want|would\s+you\s+like|prefer|pick)\s+([^.?\n]{2,160})/gi
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    const segment = m[1]
    for (const part of segment.split(/\s+\bor\b\s+/i)) {
      if (isClauseContinuation(part)) continue
      const c = trimCandidate(part)
      if (c && !isLikelySentence(c)) out.push(c)
    }
  }
  return out
}

/**
 * Bullet-list clarifications ("Which should I use:\n- realtime\n- settlement").
 * Pulls the first few short bullet items. Bullets starting with "Do you want"
 * / "Would you like" are handed to extractDisjunctionOptions instead, so this
 * only fires on short literal-option lines.
 */
function extractBulletOptions(text: string): string[] {
  const out: string[] = []
  const re = /^[\t ]*(?:[-*•]|\d+[.)])\s+([^\n]{1,100})$/gm
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    const line = m[1]
    // Skip bullets that are themselves question-shaped disjunctions; those
    // are handled by extractDisjunctionOptions so we don't double-count.
    if (/\b(?:do\s+you\s+want|would\s+you\s+like|prefer|pick)\b/i.test(line)) continue
    const c = trimCandidate(line)
    if (c && !isLikelySentence(c)) out.push(c)
  }
  return out
}

export interface ClarificationCandidate {
  id: string
  text: string
}

/**
 * Returns up to MAX_REPLIES extracted answers, or empty array when the
 * text doesn't look like a clarification or extraction yields too few.
 *
 * The caller should fall through to the gallery flow when this returns
 * an empty array OR fewer than 2 candidates (one option doesn't help —
 * "the bot asked, but we only know one thing it might want").
 */
export function extractClarificationReplies(
  botText?: string | null,
): ClarificationCandidate[] {
  if (!botText || typeof botText !== 'string') return []
  const text = botText.trim()
  if (text.length < 8) return []
  // Cheap pre-gate: must look like a question OR contain "say X or Y" OR
  // contain a bulleted list (the bot's most common non-question clarification).
  const looksClarification =
    /\?/.test(text) ||
    /\bsay\s+[^.?!]+\bor\b/i.test(text) ||
    /\*\*[^*]+\*\*/.test(text) ||
    /^[\t ]*(?:[-*•]|\d+[.)])\s+\S/m.test(text)
  if (!looksClarification) return []

  // Always run every extractor — the previous gate that skipped the
  // disjunction path when a bold match existed dropped the second half of
  // "Do you want **gross** or net?" (Codex HIGH 2026-04-21). Dedupe cleans
  // up the overlap.
  const fromBold = extractBoldOptions(text)
  const fromSay = extractSayOptions(text)
  const fromDisjunction = extractDisjunctionOptions(text)
  const fromBullets = extractBulletOptions(text)

  const merged = dedupe([
    ...fromBold,
    ...fromSay,
    ...fromDisjunction,
    ...fromBullets,
  ]).slice(0, MAX_REPLIES)

  if (merged.length < 2) return []
  return merged.map((text, idx) => ({
    id: `clar-${idx}-${text.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)}`,
    text,
  }))
}
